import { useEffect } from "react"
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom"
import { DashboardLayout } from "@/components/dashboard-layout"
import { DashboardPage } from "@/pages/dashboard"
import { AiGenerationsPage } from "@/pages/ai-generations"
import { UsersPage } from "@/pages/users"
import { UserDetailPage } from "@/pages/user-detail"
import { SeasonsPage } from "@/pages/seasons"
import { ChatEditsPage } from "@/pages/chat-edits"
import { ChatTranscriptsPage } from "@/pages/chat-transcripts"
import { ImessagePage } from "@/pages/imessage"
import { OnboardingPathsPage } from "@/pages/onboarding-paths"
import { RevenueCatPage } from "@/pages/revenuecat"
import { PrinciplesPage } from "@/pages/principles"
import { PromptsPage } from "@/pages/prompts"
import { PlaygroundPage } from "@/pages/playground"
import { AgentSandboxPage } from "@/pages/agent-sandbox"
import { MediaLibraryPage } from "@/pages/media-library"
import { OpsPage } from "@/pages/ops"
import { GlossaryPage } from "@/pages/glossary"
import { WearablesPage } from "@/pages/wearables"
import { ProgramIntegrityPage } from "@/pages/program-integrity"
import { DiagnosisDigestPage } from "@/pages/diagnosis-digest"
import { BookingAuditPage } from "@/pages/booking-audit"
import { WaitlistPage } from "@/pages/waitlist"
import { StudiosPage } from "@/pages/studios"
import { ReferralsPage } from "@/pages/referrals"
import { StudioHealthPage } from "@/pages/studio-health"
import { StudioInfoPage } from "@/pages/studio-info"
import { ClassCatalogPage } from "@/pages/class-catalog"
import { MemberReportsPage } from "@/pages/member-reports"
import { AuditPage } from "@/pages/audit"
import { EditorPage } from "@/pages/editor"
import { SettingsPage } from "@/pages/settings"
import { NotFoundPage } from "@/pages/not-found"
import { RouteErrorPage } from "@/pages/route-error"
import { LoginPage } from "@/pages/login"
import { useAuth } from "@/lib/auth"
import { clearPostLoginPath, peekPostLoginPath } from "@/lib/post-login"

function AuthGuard() {
  const { user, loading } = useAuth()
  const location = useLocation()
  const pendingPath = user ? peekPostLoginPath() : null

  useEffect(() => {
    if (user) clearPostLoginPath()
  }, [user])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  const currentPath = `${location.pathname}${location.search}${location.hash}`

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(currentPath)}`} replace />
  }

  if (pendingPath && pendingPath !== currentPath) {
    return <Navigate to={pendingPath} replace />
  }

  return <Outlet />
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    element: <AuthGuard />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <DashboardLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          { path: "/", element: <DashboardPage /> },
          { path: "/ai-generations", element: <AiGenerationsPage /> },
          { path: "/editor", element: <EditorPage /> },
          { path: "/users", element: <UsersPage /> },
          { path: "/users/:id", element: <UserDetailPage /> },
          { path: "/seasons", element: <SeasonsPage /> },
          { path: "/revenuecat", element: <RevenueCatPage /> },
          { path: "/chat", element: <ChatEditsPage /> },
          { path: "/chat-transcripts", element: <ChatTranscriptsPage /> },
          { path: "/imessage", element: <ImessagePage /> },
          { path: "/onboarding-paths", element: <OnboardingPathsPage /> },
          { path: "/principles", element: <PrinciplesPage /> },
          { path: "/prompts", element: <PromptsPage /> },
          { path: "/playground", element: <PlaygroundPage /> },
          { path: "/agent-sandbox", element: <AgentSandboxPage /> },
          { path: "/media", element: <MediaLibraryPage /> },
          { path: "/ops", element: <OpsPage /> },
          { path: "/glossary", element: <GlossaryPage /> },
          { path: "/wearables", element: <WearablesPage /> },
          { path: "/integrity", element: <ProgramIntegrityPage /> },
          { path: "/audit", element: <AuditPage /> },
          { path: "/diagnosis", element: <DiagnosisDigestPage /> },
          { path: "/booking-audit", element: <BookingAuditPage /> },
          { path: "/waitlist", element: <WaitlistPage /> },
          { path: "/studios", element: <StudiosPage /> },
          { path: "/referrals", element: <ReferralsPage /> },
          { path: "/studio-health", element: <StudioHealthPage /> },
          { path: "/studio-info", element: <StudioInfoPage /> },
          { path: "/class-catalog", element: <ClassCatalogPage /> },
          { path: "/member-reports", element: <MemberReportsPage /> },
          { path: "/settings", element: <SettingsPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
])
